import { useMemo, useState } from "react";
import "@/App.css";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster } from "sonner";
import { ArrowRight, ArrowLeft, Sparkles } from "lucide-react";
import { TrustBanner } from "@/components/TrustBanner";
import { AppGrid } from "@/components/AppGrid";
import { QuickTaps } from "@/components/QuickTaps";
import { Reveal } from "@/components/Reveal";
import ResultPage from "@/components/ResultPage";
import { ConsentBanner } from "@/components/ConsentBanner";
import { calculate } from "@/lib/valuation";
import { QUESTIONS } from "@/data/quiz";
import { trackEvent } from "@/lib/analytics";

const INITIAL = {
  apps: [],
  age: null,
  shopping: null,
  loyalty: null,
  streaming: null,
  footprint: null,
};

function App() {
  const [step, setStep] = useState(0);
  const [quiz, setQuiz] = useState(INITIAL);
  const result = useMemo(() => calculate(quiz), [quiz]);

  const toggleApp = (id) =>
    setQuiz((q) => ({
      ...q,
      apps: q.apps.includes(id) ? q.apps.filter((a) => a !== id) : [...q.apps, id],
    }));

  const selectAnswer = (key, val) => setQuiz((q) => ({ ...q, [key]: val }));

  const allAnswered = QUESTIONS.every((q) => quiz[q.key]);
  const canNext = (step === 1 && quiz.apps.length > 0) || (step === 2 && allAnswered);

  const goNext = () => {
    if (step === 2) trackEvent("reveal_number", { value: result.total, currency: "AUD" });
    setStep((s) => s + 1);
  };

  const restart = () => {
    setQuiz(INITIAL);
    setStep(0);
  };

  return (
    <div className="relative min-h-screen bg-base text-zinc-900">
      <Toaster theme="light" position="top-center" richColors />
      <ConsentBanner />
      {/* ambient pastel glows */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="animate-pulse-glow absolute -left-24 top-10 h-72 w-72 rounded-full bg-lime/40 blur-[110px]" />
        <div className="animate-pulse-glow absolute -right-24 top-1/3 h-72 w-72 rounded-full bg-aqua/30 blur-[110px]" />
        <div className="animate-pulse-glow absolute -bottom-24 left-1/4 h-72 w-72 rounded-full bg-gold/20 blur-[110px]" />
      </div>

      <main className="relative mx-auto flex min-h-screen w-full max-w-md flex-col px-6 pb-28 pt-8">
        {/* header */}
        <div className="mb-6 flex items-center justify-between">
          <button
            data-testid="logo-home"
            onClick={restart}
            className="flex items-center gap-2 font-display text-sm font-bold tracking-tight text-zinc-900"
          >
            <span className="h-2.5 w-2.5 rounded-full bg-lime shadow-[0_0_10px_#CCFF00]" />
            whatsmydataworth
          </button>
          {step > 0 && step < 3 && (
            <button
              data-testid="back-btn"
              onClick={() => setStep((s) => s - 1)}
              className="flex items-center gap-1 text-xs font-medium text-zinc-500"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back
            </button>
          )}
        </div>

        {/* progress */}
        {step >= 1 && step <= 3 && (
          <div className="mb-8 flex gap-1.5">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`h-1.5 flex-1 rounded-full transition-colors ${
                  step >= s ? "bg-lime" : "bg-black/10"
                }`}
              />
            ))}
          </div>
        )}

        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div
              key="landing"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="flex flex-1 flex-col justify-center"
            >
              <div className="animate-floaty mb-6 inline-flex w-fit items-center gap-2 rounded-full border border-limeink/20 bg-lime/25 px-3 py-1.5 text-xs font-bold text-limeink">
                <Sparkles className="h-3.5 w-3.5" /> 60-second data check
              </div>
              <h1 className="font-display text-5xl font-black leading-[0.95] tracking-tighter text-zinc-900">
                What's your <span className="text-limeink">data</span> actually{" "}
                <span className="text-aquaink">worth?</span>
              </h1>
              <p className="mt-5 text-base text-zinc-600">
                Find out how much companies earn from <span className="font-semibold text-zinc-900">you</span> every
                year. No signup, no forms — just taps.
              </p>
              <TrustBanner className="mt-6" />
              <button
                data-testid="start-btn"
                onClick={() => {
                  trackEvent("start_quiz");
                  setStep(1);
                }}
                className="mt-8 flex items-center justify-center gap-2 rounded-full bg-lime py-4 font-display text-lg font-bold text-black shadow-[0_12px_35px_rgba(204,255,0,0.55)] transition-transform active:scale-95"
              >
                Start the check <ArrowRight className="h-5 w-5" strokeWidth={2.5} />
              </button>
              <p className="mt-4 text-center text-xs text-zinc-400">
                Playful but credible • Made for Australians 🇦🇺
              </p>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div
              key="apps"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
            >
              <h2 className="font-display text-3xl font-black tracking-tight text-zinc-900">
                Which apps do you use?
              </h2>
              <p className="mb-6 mt-2 text-sm text-zinc-500">Tap all that apply.</p>
              <AppGrid selected={quiz.apps} onToggle={toggleApp} />
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="taps"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
            >
              <h2 className="font-display text-3xl font-black tracking-tight text-zinc-900">
                A few quick taps
              </h2>
              <p className="mb-6 mt-2 text-sm text-zinc-500">One pick each — no wrong answers.</p>
              <QuickTaps answers={quiz} onSelect={selectAnswer} />
            </motion.div>
          )}

          {step === 3 && (
            <motion.div key="reveal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <Reveal amount={result.total} onContinue={() => setStep(4)} />
            </motion.div>
          )}

          {step === 4 && (
            <motion.div key="result" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <ResultPage amount={result.total} result={result} quiz={quiz} />
              <button
                data-testid="restart-btn"
                onClick={restart}
                className="mt-4 w-full rounded-full border border-black/10 bg-white py-3 text-sm font-medium text-zinc-500 shadow-sm transition-colors hover:text-zinc-900"
              >
                Start over
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* sticky next CTA for steps 1-2 */}
      {(step === 1 || step === 2) && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-black/10 bg-white/80 backdrop-blur-xl">
          <div className="mx-auto flex max-w-md items-center gap-3 px-6 py-4">
            <TrustBanner className="hidden flex-1 sm:flex" />
            <button
              data-testid="next-step-btn"
              disabled={!canNext}
              onClick={goNext}
              className="flex flex-1 items-center justify-center gap-2 rounded-full bg-lime py-3.5 font-display font-bold text-black shadow-[0_8px_22px_rgba(204,255,0,0.4)] transition-opacity disabled:opacity-40 sm:flex-none sm:px-8"
            >
              {step === 2 ? "Reveal my number" : "Next"}
              <ArrowRight className="h-5 w-5" strokeWidth={2.5} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
